import moment from 'moment';
import history from '../../../utils/history';
import MobileDetect from 'mobile-detect';
import { getTimeZoneDate } from 'web-common/scripts/utils/DateHelper';
import { useEffect, useRef } from 'react';
import { getInternalWebsiteBaseUrl } from 'web-common/scripts/utils/whitelabelUtils';
import webcastingType from 'web-common/scripts/enums/webcastingTypeEnum';
import { stringify } from 'query-string';
import { getVirtualSessionEndPoint } from '../ApiUtil';
import FeatureToggleUtils from 'FeatureToggleUtils';

export const SESSION_GRACE_TIME = 20; // 20 min
export const PRE_SESSION_LIVE_TIME = 5; // 5 min
export const ACCESS_ERROR_CHECKING_TIME = 24*60*60*1000; // 24 hours
export const MAX_CONCURRENT_STREAMS = 1;

export const accessErrorTypes = {
    unauthenticated: 'UNAUTHENTICATED',
    ticketType: 'TICKET_TYPE',
    contactList: 'CONTACT_LIST',
    sessionRegistration: 'SESSION_REGISTRATION',
};

export function isSessionEnded(session, timeZone){
    if (!session?.endDate) {
        return false;
    }
    const now = moment().tz(timeZone);
    const sessionEndTime = getTimeZoneDate(session.endDate, session.endMinute, timeZone);
    return now.isAfter(sessionEndTime);
}

export function isSessionStartsInMorethan(session, numberOfmin, timeZone){
    if (!session?.startDate) {
        return false;
    }
    const sessionStartTime = getTimeZoneDate(session.startDate, session.startMinute, timeZone);
    const now = moment().tz(timeZone);

    if (sessionStartTime) {
        const diff = sessionStartTime.diff(now, 'seconds');
        return diff >= (numberOfmin * 60);
    }
}

export function isSessionEndsInMoreThan(session, numberOfmin, timeZone){
    if (!session?.endDate) {
        return false;
    }
    const sessionEndTime = getTimeZoneDate(session.endDate, session.endMinute, timeZone);
    const now = moment().tz(timeZone);

    if (sessionEndTime) {
        const diff = sessionEndTime.diff(now, 'seconds');
        return diff >= (numberOfmin * 60);
    }

    return false;
}

export function isSessionEndedMoreThan(session, numberOfmin, timeZone){
    if (!session?.endDate) {
        return false;
    }
    const sessionEndTime = getTimeZoneDate(session.endDate, session.endMinute, timeZone);
    const now = moment().tz(timeZone);
    const diff = now.diff(sessionEndTime, 'seconds');
    return (diff >= (numberOfmin * 60));
}

export function isSessionLive(session, timeZone) {
    if (!session.startDate) {
        return false;
    }
    const isSessionInMoreThanPreliveTime = isSessionStartsInMorethan(session, PRE_SESSION_LIVE_TIME, timeZone);
    const isSessionOver = isSessionEnded(session, timeZone);
    return !isSessionInMoreThanPreliveTime && !isSessionOver;
}

export function loadSessionPage({ event, sessionId, reloadCsp = false, reloadPage = false }) {
    const url = reloadCsp ? `agenda/session/${sessionId}` : getInternalWebsiteBaseUrl(event, `agenda/session/${sessionId}`);
    if (reloadPage) {
        window.location.href = url;
    } else {
        history.push(url);
    }
}

export const getSessionPageUrl = (event, sessionId) => getInternalWebsiteBaseUrl(event, `agenda/session/${sessionId}`);

export function redirectToBroadcast(url) { // Used for broadcasts on external domains
    window.location.assign(url);
}

export const hasTouchScreen = function() {
    let result = false;
    const mediaFeature = '(pointer:coarse)'; // https://developer.mozilla.org/en-US/docs/Web/CSS/@media/pointer
    const mediaQuery = matchMedia?.(mediaFeature);

    if (mediaQuery?.media === mediaFeature) {
        result = !!mediaQuery.matches;
    } else if (window.PointerEvent && 'maxTouchPoints' in navigator) {
        result = navigator.maxTouchPoints > 1;
    } else if ('orientation' in window) {
        result = true;
    }

    return result;
};

export const isMobile = function() {
    const { userAgent } = navigator;

    const md = new MobileDetect(userAgent);

    // MobileDetect library relays on User Agent and returns falsy negative for some ios devices
    // Let's check for touch screen as well (Duck typing, if it has touch screen, most probably it's Iphone or Ipad)
    if (!md.mobile() && userAgent.match(/Mac/)) {
        return hasTouchScreen();
    }

    return !!md.mobile();
};

export const buildNewrowUrl = (newrowSessionToken) => {
    const kalturaPartnerId ='2711221';
    const newrowSrcUrl = 'kaf.kaltura.com/virtualEvent/launch/ks/';
    return `https://${kalturaPartnerId}.${newrowSrcUrl}${newrowSessionToken}`;
};

export function getRestrictionReason({ reason, parsed = false, tickets = [] }) {
    let errorType;

    if (parsed) {
        errorType = reason;
    } else {
        const errorParts = reason.message.split('---');
        errorType = errorParts && errorParts.length > 0 && errorParts[1];
    }

    const getTicketErrorMessage = () => {
        if (!tickets.length) {
            return 'This session is only available for specific ticket types';
        } else if (tickets.length === 1) {
            return `Your ticket type (${tickets[0]?.ticketName}) does not have access to this session`;
        } else {
            return "Your ticket types don't have access to this session";
        }
    };

    switch (errorType){
    case accessErrorTypes.sessionRegistration:
        return 'You need to save a seat to join this session';
    case accessErrorTypes.ticketType:
        return getTicketErrorMessage();
    case accessErrorTypes.contactList:
        return 'This session is only available for specific attendees';
    case accessErrorTypes.unauthenticated:
        return 'You need to be registered and logged in to join this session';
    default:
        return 'This broadcast is only available for authorized attendees';
    }
}

export const getWebcastingIframeUrl = ({
    token: ks,
    mediaEntryId: webcastEntryId,
    playerUIConf: uiConfId,
    userId,
    isBizzaboQnaActive,
}, event, partnerId = 2711221) => getInternalWebsiteBaseUrl(event,
    `webcasting/iframe?${stringify({
        ks,
        webcastEntryId,
        uiConfId,
        partnerId,
        userId,
        isBizzaboQnaActive,
    })}`);

export const providerTypes = {
    VIMEO: 'VIMEO',
    YOUTUBE: 'YOUTUBE',
    LIVESTREAM: 'LIVESTREAM',
    BRIGHTCOVE: 'BRIGHTCOVE',
    HLS: 'HLS',
    CUSTOM: 'CUSTOM',
};

export const getTimeFromSessionStart = ({startDate, startMinute, eventTimeZone}) => {
    const now = moment().tz(eventTimeZone);
    const sessionStartTime = getTimeZoneDate(startDate, startMinute, eventTimeZone);
    const diff = now.diff(sessionStartTime, 'seconds');
    const timeFromSessionStart = moment.duration(diff, 'seconds').format('hh:mm:ss', {trim: false});
    return timeFromSessionStart;
};

export const getTimeFromSessionStartInSeconds = ({startDate, startMinute, eventTimeZone }) => {
    const now = moment().tz(eventTimeZone);
    const sessionStartTime = getTimeZoneDate(startDate, startMinute, eventTimeZone);
    const timeFromSessionStart = now.diff(sessionStartTime, 'seconds');
    return timeFromSessionStart;
};

export const getTimeToSessionStart = ({ startDate, startMinute, eventTimeZone }) => {
    const now = moment().tz(eventTimeZone);
    const sessionStartTime = getTimeZoneDate(startDate, startMinute, eventTimeZone);
    const diff = sessionStartTime.diff(now, 'seconds');
    const timeToSessionStart = moment.duration(diff, 'seconds').format('dd:hh:mm:ss', {trim: false});
    return timeToSessionStart;
};

export const getTimeToSessionStartInMs = ({ startDate, startMinute, eventTimeZone }) => {
    const now = moment().tz(eventTimeZone);
    const sessionStartTime = getTimeZoneDate(startDate, startMinute, eventTimeZone);
    return sessionStartTime.diff(now);
};

export const getSessionDuration = ({ startDate, startMinute, endDate, endMinute, eventTimeZone }) => {
    const sessionStartTime = getTimeZoneDate(startDate, startMinute, eventTimeZone);
    const sessionEndTime = getTimeZoneDate(endDate, endMinute, eventTimeZone);
    const diff = sessionEndTime.diff(sessionStartTime, 'seconds');
    const sessionDuration = moment.duration(diff, 'seconds').format('hh:mm:ss', {trim: false});
    return sessionDuration;
};

export const getSessionDurationInMs = ({ startDate, startMinute, endDate, endMinute, eventTimeZone }) => {
    const sessionStartTime = getTimeZoneDate(startDate, startMinute, eventTimeZone);
    const sessionEndTime = getTimeZoneDate(endDate, endMinute, eventTimeZone);
    return sessionEndTime.diff(sessionStartTime);
};

export const isSessionDurationOver = ({ startDate, startMinute, endDate, endMinute, eventTimeZone, graceTime })=>{
    const now = moment().tz(eventTimeZone);
    const sessionStartTime = getTimeZoneDate(startDate, startMinute, eventTimeZone);
    const sessionEndTime = getTimeZoneDate(endDate, endMinute, eventTimeZone);
    const timeFromSessionStart = now.diff(sessionStartTime, 'seconds');
    const sessionDuration = sessionEndTime.diff(sessionStartTime, 'seconds');
    return graceTime ?
        timeFromSessionStart >= sessionDuration + graceTime :
        timeFromSessionStart >= sessionDuration;
};


export const getPercentOfTimeElapsed = ({startDate, startMinute, endDate, endMinute, eventTimeZone }) => {
    const now = moment().tz(eventTimeZone);
    const sessionStartTime = getTimeZoneDate(startDate, startMinute, eventTimeZone);
    const sessionEndTime = getTimeZoneDate(endDate, endMinute, eventTimeZone);
    const totalDiff = sessionEndTime.diff(sessionStartTime);
    const currentDiff = now.diff(sessionStartTime);
    const percentOfTimeElapsed = (currentDiff / totalDiff) * 100;
    if (percentOfTimeElapsed <= 0) return 0;
    if (percentOfTimeElapsed >= 100) return 100;
    return percentOfTimeElapsed;
};

export const useInterval =(callback, delay, triggerOnFirstTick = true)=>{
    const savedCallback = useRef();
    // Remember the latest callback.
    useEffect(() => {
        savedCallback.current = callback;
    }, [callback]);
    // Set up the interval.
    useEffect(() => {
        triggerOnFirstTick && savedCallback.current();
        function tick() {
            savedCallback.current();
        }
        if (delay !== null) {
            let id = setInterval(tick, delay);
            return () => clearInterval(id);
        }
    }, [delay]);
};

export const getTimeToSessionEndMS = ({ endDate, endMinute, eventTimeZone, graceTime }) => {
    const sessionEndTime = getTimeZoneDate(endDate, endMinute, eventTimeZone);
    const now = moment().tz(eventTimeZone);
    const diff = sessionEndTime.diff(now, 'milliseconds');
    return diff >= 0
        ? diff
        : sessionEndTime.add(graceTime, 'minutes').diff(now, 'milliseconds');
};

export const getSessionTimeMetrics = ({ endDate, endMinute, eventTimeZone, graceTime }) => {
    const sessionEndTime = getTimeZoneDate(endDate, endMinute, eventTimeZone)?.valueOf();
    const sessionEndGrace = moment(sessionEndTime).tz(eventTimeZone).add(graceTime, 'minutes')?.valueOf();
    return { sessionEndTime, sessionEndGrace };
};

export const getCurrentTime = (eventTimeZone) => moment().tz(eventTimeZone).valueOf();

export const getDateInIsoFormat = (date, zone) => moment(date).tz(zone).utc().toISOString();

export const getTimeToSessionEndWithDelayMs = (session, delay, eventTimeZone) => {
    const sessionEndTime = getTimeZoneDate(session.endDate, session.endMinute, eventTimeZone);
    const now = moment().tz(eventTimeZone);
    return sessionEndTime.add(delay, 'minutes').diff(now, 'milliseconds');
};

export const getCurrentTimeDiffRelativeToSessionTimeInMins = ({ date, minutes, eventTimeZone }) => {
    const now = moment().tz(eventTimeZone);
    const tzDate = getTimeZoneDate(date, minutes, eventTimeZone);
    const diff = now.diff(tzDate, 'minutes');
    return diff;
};

// eslint-disable-next-line no-useless-escape
export const isValidUrl = value => /https?:\/\/(www\.)?[-a-zA-Z0-9@:%._\+~#=]{1,256}\.[a-z]{2,63}\b([-a-zA-Z0-9@:%_\+.~#?&/=]*)/i.test(value);

export const getValueAfterLastSlash = (string = '') => {
    if (isValidUrl(string)) {
        return string.trim().replace(/\/+$/, '').split('/').pop();
    }

    return string;
};

export const buildCloudinaryVideoUrls = (recordingUrl, hlsEnabled) => {
    const extractCloudinaryFolderWithPublicId = recordingUrl => recordingUrl.split('/').slice(-2).join('/').split('.')[0];
    const folderWithPublicId = extractCloudinaryFolderWithPublicId(recordingUrl);
    const mime = recordingUrl.split('.').pop();
    const cloudinaryAccountName = 'bizzabo-demo';
    const streamingProfile = 'sp_full_hd_wifi';
    const videoOriginalUrl = `https://assets-ext.bizzabo.com/${cloudinaryAccountName}/video/upload/${folderWithPublicId}.${mime}`;
    if (!hlsEnabled) {
        return [videoOriginalUrl];
    }
    const videoHlsUrl = `https://assets-ext.bizzabo.com/${cloudinaryAccountName}/video/upload/${streamingProfile}/${folderWithPublicId}.m3u8`;
    return [videoHlsUrl, videoOriginalUrl];
};

export const videoOrigins = {
    CLOUDINARY: 'CLOUDINARY',
    KALTURA: 'KALTURA'
};

export const playingComponent = {
    BroadcastRecords: 'BroadcastRecords',
    PrerecordedVideo: 'PrerecordedVideo',
    Preroll: 'Preroll',
};

export const buildKalturaRecordingUrl = (recordingUrl) => {
    // http://cdnapi.kaltura.com/p/2711221/sp/0/playManifest/entryId/1_icbl5pbp/format/url/protocol/http - original link
    // we just take the entryId
    if (!recordingUrl) {
        return;
    }
    const kalturaPartnerId ='2711221';
    const entryId = recordingUrl.split('/')[9];
    return entryId && `https://cdnapi.kaltura.com/p/${kalturaPartnerId}/sp/${kalturaPartnerId}00//playManifest/entryId/${entryId}/protocol/https/format/applehttp/a.m3u8`;
};

const buildKalturaOriginalUrl = (rawRecordUrl) => {
    if (!rawRecordUrl) {
        return;
    }

    const regex = /http/gi;
    return rawRecordUrl.replace(regex, 'https');
};

export const buildRecordsUrls = ({ rawRecordUrl, hlsEnabled, videoOrigin, context }) => {
    if (!rawRecordUrl) return;
    if (!videoOrigin) {
        return [rawRecordUrl];
    }
    if (videoOrigin === videoOrigins.CLOUDINARY) {
        return buildCloudinaryVideoUrls(rawRecordUrl, hlsEnabled);
    }

    if (videoOrigin === videoOrigins.KALTURA) {
        if (context === playingComponent.BroadcastRecords) {
            const kalturaUrl = buildKalturaRecordingUrl(rawRecordUrl);
            if (!kalturaUrl) return;
            return [kalturaUrl];
        }
        if (context === playingComponent.PrerecordedVideo) {
            const kalturaUrl = buildKalturaOriginalUrl(rawRecordUrl);
            if (!kalturaUrl) return;
            return [kalturaUrl];
        }
    }
};

export const buildVodRecordsUrls = ({ virtualData, context }) => {
    const { recordingUrl: rawRecordUrl, availableHls: hlsEnabled } = virtualData;
    let videoOrigin = undefined;
    if (rawRecordUrl?.includes('cloudinary.com')) {
        videoOrigin = videoOrigins.CLOUDINARY;
    } else if (rawRecordUrl?.includes('kaltura.com')) {
        videoOrigin = videoOrigins.KALTURA;
    }
    return buildRecordsUrls({ rawRecordUrl, hlsEnabled, videoOrigin, context });
};

export const hashStringTo32bitInt = str => {
    let hash = 0, i, chr;
    for (i = 0; i < str.length; i++) {
        chr = str.charCodeAt(i);
        hash = ((hash << 5) - hash) + chr;
        hash |= 0; // Convert to 32bit integer
    }
    return Math.abs(hash);
};

export const attendeeSessionStatus = {
    CheckIn: 'CheckIn', CheckOut: 'CheckOut'
};

export const loadVirtualSessionPage = async(sessionId, event) => {
    try {
        const virtualSessionDetails = await getVirtualSessionEndPoint({sessionId, eventId: event.id});
        loadSessionPage({ event, sessionId, reloadPage: virtualSessionDetails.type === webcastingType.ZOOM });
    } catch (e) {
        loadSessionPage({ event, sessionId });
    }
};


export const PreSessionExperienceTypes = {
    'NONE': 'NONE',
    'LOUNGE': 'LOUNGE',
    'PRE_ROLL': 'PRE_ROLL'
};

export const checkIsLoungeAvailable = (session, timeZone) => {
    const isLoungeWaitingRoomAvailable = session?.waitingRoom?.type === PreSessionExperienceTypes.LOUNGE;
    const sessionHasNotStartedYet = isSessionStartsInMorethan(session, 0, timeZone);
    const isSessionOver = isSessionEnded(session, timeZone);
    const isLivestream = isLivestreamType(session);
    const isLoungeTimeHasCome = !isSessionOver && isLoungeWaitingRoomAvailable && !isSessionStartsInMorethan(session, session?.waitingRoom?.startTime, timeZone);
    return isLivestream ? isLoungeTimeHasCome : sessionHasNotStartedYet && isLoungeTimeHasCome;
};

export const isLivestreamType = (session) => {
    const sessionVirtualType = session?.virtualSessionDetails?.type;
    const isMeetup = session?.virtualSessionDetails?.sessionType === 'MEETUP';

    return sessionVirtualType === webcastingType.VIDEO_PROVIDER || (sessionVirtualType === webcastingType.INTERACTIVE_VIDEO_PROVIDER && !isMeetup);
};

export const getIsSessionStartDynamic = (session) => session.virtualSessionDetails?.type === webcastingType.INTERACTIVE_VIDEO_PROVIDER || session.virtualSessionDetails?.type === webcastingType.VIDEO_PROVIDER;

export const isPrerecordedVirtualSessionType = (session) =>
    session?.virtualSessionDetails?.type === webcastingType.SIMULIVE;

export const getIsVirtualSectionEnabled = (session, event, sessionViewType) => {
    const { enableVirtualSession, enableRecordingSession, recordingSession = {} } = session || {};

    if (sessionViewType === 'sessionPreviewWindow') {
        return false;
    }

    const { recordingIsReady = false } = recordingSession;

    const timeZone = event && event['time-zone-id'];

    const isSessionOver = isSessionEnded(session, timeZone);
    const isSessionPassedGraceTime = isSessionEndedMoreThan(session, SESSION_GRACE_TIME, timeZone);
    const isPrerecorded = isPrerecordedVirtualSessionType(session);
    const isFeatureSessionVirtualElementEnabled = FeatureToggleUtils.isFeatureEnabled('feature-virtual-enable-virtual-suite');

    const recordingReady = isSessionOver && enableRecordingSession && recordingIsReady;
    const graceShownForVirtualSection = isPrerecorded ? !isSessionOver : !isSessionPassedGraceTime;
    const shouldRenderVirtualSection = isFeatureSessionVirtualElementEnabled && enableVirtualSession && graceShownForVirtualSection && !recordingReady;

    return shouldRenderVirtualSection;
};


export const getIsOnDemandSectionEnabled = (session, event, sessionViewType) => {
    if (sessionViewType === 'sessionPreviewWindow') {
        return false;
    }

    const {
        enableRecordingSession,
        recordingSession = {},
    } = session || {};

    const sessionIsUnscheduled = !session?.startDate;
    const eventStartDate = event && event['start-date'];
    const timeZone = event && event['time-zone-id'];
    const isSessionOver = isSessionEnded(session, timeZone);
    const isSessionLiveNow = isSessionLive(session, timeZone);
    const isSessionPassedGraceTime = isSessionEndedMoreThan(session, SESSION_GRACE_TIME, timeZone);
    const eventStarted = Date.now() > eventStartDate;
    const recordingReady = (isSessionOver || (isSessionLiveNow && !session.enableVirtualSession))
        && enableRecordingSession && recordingSession?.recordingIsReady;
    const shouldRenderOnDemandSection =
        enableRecordingSession
        && (isSessionPassedGraceTime || (eventStarted && sessionIsUnscheduled) || recordingReady);
    return shouldRenderOnDemandSection;
};
